<template lang="pug">
el-main(v-loading="loading")
  h2(v-if="id") Airport {{form.code | uppercase}}
  h2(v-else) New Airport

  el-form(ref="form" :model="form" :rules="rules" label-width="120px" :disabled="formIsDisabled")
    el-form-item.text-right
      el-button(type="primary" @click="submit" :disabled="formIsDisabled") Save
      el-button(
        v-if="id"
        type="danger"
        @click="deleteAirport"
        :disabled="formIsDisabled") Delete
      el-button(v-if="id && !form.published"
        type="success"
        @click="publish"
        :disabled="formIsDisabled") Publish
      el-button(v-if="id && form.published"
        type="warning"
        @click="unpublish"
        :disabled="formIsDisabled") Unpublish
    el-form-item(label="Airport Name" prop="name")
      el-input(v-model="form.name" placeholder="Airport Name")
    el-form-item(label="Airport Code" prop="code")
      el-input(v-model="form.code" placeholder="Airport Code (i.e. EWR)"
       :disabled="form.published")
    el-form-item(label="Terminal" prop="terminal")
      el-input(v-model="form.terminal" placeholder="Airport Terminal")
    ImageUpload(
      label="Home Image"
      :uploadURL="'/s3/signed-upload-url'"
      v-model="form.homeImageUrl"
    )
    ImageUpload(
      label="Promo Image"
      :uploadURL="'/s3/signed-upload-url'"
      v-model="form.promoImageUrl"
    )
    el-form-item(label="Sections")
      .text-right
        el-button(@click="addSection") + New Section
    el-form-item(
      v-for="(section, index) in form.sections"
      :label="'Section ' + (index + 1)"
      :key="'Section' + index"
      :prop="'sections[' + index + '].name'"
      :rules="{required: true, message: 'Section name is required', trigger: 'blur'}"
    )
      el-input(v-model="section.name" :placeholder="'Section ' + (index + 1) + ' name'")
        div(slot="append")
          el-button(
            type="danger"
            icon="el-icon-delete"
            @click.prevent="removeSection(section)"
          )
</template>

<script>
import AirportsService from '@/services/AirportsService';
import ImageUpload from '@/components/ImageUpload';
import EventBus from '@/helpers/event-bus';

export default {
  components: { ImageUpload },
  filters: {
    uppercase(string) {
      if (!string) {
        return '';
      }
      return string.toUpperCase();
    },
  },
  props: {
    id: {
      default: null,
      type: String,
    },
  },
  data() {
    return {
      formIsDisabled: false,
      form: {
        name: '',
        code: '',
        terminal: '',
        sections: [],
        homeImageUrl: '',
        promoImageUrl: '',
        published: false,
      },
      rules: {
        name: [
          {
            required: true,
            message: 'Please input Airport Name',
            trigger: 'blur',
          },
          {
            min: 3,
            message: 'Length should more than 3',
            trigger: 'blur',
          },
        ],
        code: [
          {
            required: true,
            message: 'Please input Airport Code',
            trigger: 'blur',
          },
          {
            min: 3,
            max: 3,
            message: 'Length should be 3',
            trigger: 'change',
          },
        ],
        terminal: [
          {
            required: true,
            message: 'Please input Airport Terminal',
            trigger: 'blur',
          },
        ],
      },
      homeFileList: [],
      promoFileList: [],
      loading: false,
    };
  },
  watch: {
    $route() {
      this.fetchData();
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    submit() {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.formIsDisabled = true;
          this.loading = true;
          if (!this.id) {
            AirportsService.createAirport(this.form)
              .then(({ data }) => {
                this.$message({
                  type: 'success',
                  message: 'Submitted',
                });
                this.$router.push({
                  name: 'AdminAirport',
                  params: { id: data.id },
                });
              })
              .catch(error => {
                this.$message({
                  type: 'error',
                  message:
                    error.response.data.errorMessage || error.response.data,
                });
              })
              .finally(() => {
                EventBus.$emit('airports-list-modified');
                this.formIsDisabled = false;
                this.loading = false;
              });
          } else {
            AirportsService.updateAirport(this.form)
              .then(() => {
                this.$message({
                  type: 'success',
                  message: 'Saved',
                });
              })
              .catch(error => {
                this.$message({
                  type: 'error',
                  message:
                    error.response.data.errorMessage || error.response.data,
                });
              })
              .finally(() => {
                EventBus.$emit('airports-list-modified');
                this.formIsDisabled = false;
                this.loading = false;
              });
          }
        } else {
          this.$message({
            type: 'error',
            message: 'Missing required fields',
          });
        }
      });
    },
    resetForm() {
      this.$refs.form.resetFields();
    },
    addSection() {
      this.form.sections.push({
        name: '',
      });
    },
    removeSectionItem(item) {
      const index = this.form.sections.indexOf(item);
      if (index !== -1) {
        this.form.sections.splice(index, 1);
      }
    },
    removeSection(section) {
      if (!this.id) {
        this.removeSectionItem(section);
        return;
      }

      const message = `Are you sure you want to delete this section?
        All associated shopping, dining, services, and special offer data will
        be permanently removed.`;
      this.$confirm(message, 'Warning', {
        type: 'warning',
        confirmButtonText: 'Yes, Delete!',
      })
        .then(() => {
          this.removeSectionItem(section);
        })
        .catch(err => {
          this.$message({
            type: 'info',
            message: err,
          });
        });
    },
    fetchData() {
      this.formIsDisabled = false;
      this.loading = false;
      if (!this.id) {
        this.form.name = '';
        this.form.code = '';
        this.form.terminal = '';
        this.form.sections = [];
        this.form.homeImageUrl = '';
        this.form.promoImageUrl = '';
        this.form.published = false;
        setTimeout(() => {
          this.resetForm();
        }, 0);

        return;
      }

      this.loading = true;
      AirportsService.getAirport(this.id)
        .then(({ data }) => {
          this.form = Object.assign({}, this.form, data);
        })
        .catch(error => {
          this.$message({
            type: 'error',
            message: error.response
              ? error.response.data.errorMessage
              : error.message,
          });
          setTimeout(() => {
            this.$router.push({ name: 'NewAdminAirport' });
          }, 1000);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deleteAirport() {
      const message = `Are you sure you want to delete this airport?
      All associated shopping, dining, services, and special offer data will
      be permanently removed.`;
      this.$confirm(message, 'Warning', {
        type: 'warning',
        confirmButtonText: 'Yes, Delete!',
      })
        .then(() => {
          this.loading = true;
          AirportsService.deleteAirport(this.id)
            .then(() => {
              this.$router.push({ name: 'NewAdminAirport' });
            })
            .catch(error => {
              this.$message({
                type: 'error',
                message: error.response
                  ? error.response.data.errorMessage
                  : error.message,
              });
            })
            .finally(() => {
              this.loading = false;
            });
        })
        .catch(err => {
          this.$message({
            type: 'info',
            message: err,
          });
        })
        .finally(() => {
          setTimeout(() => {
            EventBus.$emit('airports-list-modified');
          }, 10);
        });
    },
    publish() {
      this.loading = true;
      AirportsService.publishAirport(this.id)
        .then(() => {
          this.form.published = true;
          setTimeout(() => {
            EventBus.$emit('airports-list-modified');
          }, 10);
        })
        .catch(error => {
          this.$message({
            type: 'error',
            message: error.response
              ? error.response.data.errorMessage
              : error.message,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    unpublish() {
      this.loading = true;
      AirportsService.unpublishAirport(this.id)
        .then(() => {
          this.form.published = false;
          setTimeout(() => {
            EventBus.$emit('airports-list-modified');
          }, 10);
        })
        .catch(error => {
          this.$message({
            type: 'error',
            message: error.response
              ? error.response.data.errorMessage
              : error.message,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="stylus" scoped>
.el-input-group__append
  .el-button--danger
    color red
</style>




// WEBPACK FOOTER //
// src/components/AdminAirport.vue